import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Button from 'components/shared/button';
import styles from './styles.module.scss';

const TestimonialsSection = ({
  testimonialsTitle = null,
  buttonLabel = null,
  buttonLink = null,
  testimonials = [],
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay()]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <div className={styles.testimonialSection}>
      <div className={styles.testimonialCard}>
        <div className={styles.testimonialHeader}>
          {testimonialsTitle && <h3 className={styles.testimonialsTitle}>{testimonialsTitle}</h3>}
        </div>
        <div className={styles.testimonialSliderWrapper}>
          <div className={styles.embla} ref={emblaRef}>
            <div className={styles.embla__container}>
              {testimonials.map((item, idx) => (
                <div className={styles.embla__slide} key={idx}>
                  <div className={styles.testimonialContent}>
                    <div className={styles.quoteText}>{item.description}</div>
                    <div className={styles.authorInfo}>
                      {item.authorPhoto && (
                        <img
                          src={item.authorPhoto.url}
                          alt={item.authorName}
                          className={styles.authorPhoto}
                        />
                      )}
                      <div>
                        <div className={styles.authorName}>{item.authorName}</div>
                        <div className={styles.authorTitle}>{item.authorTitle}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.embla__dots}>
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={cn(styles.embla__dot, {
                  [styles['embla__dot--selected']]: index === selectedIndex,
                })}
                type="button"
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className={styles.customerStoriesLink}>
        {buttonLabel && buttonLink && (
          <Button to={buttonLink} theme="accent-primary" size="sm">
            {buttonLabel}
          </Button>
        )}
      </div>
    </div>
  );
};


TestimonialsSection.propTypes = {
  testimonialsTitle: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      authorName: PropTypes.string.isRequired,
      authorTitle: PropTypes.string.isRequired,
      authorPhoto: PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
      }),
    })
  ),
};

export default TestimonialsSection;
